import { useMemo, useState } from "react";
import type { JournalEntry, LedgerAccount, PlanItemView } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { money, payeeName } from "../../shared/lib/format";
import { rankedFilter } from "../../shared/lib/search";
import { Modal } from "../../shared/ui/Modal";
import { assignTransaction } from "./api";

interface AssignDialogProps {
  open: boolean;
  onClose: () => void;
  bookID: string;
  view: PlanItemView;
  transactions: JournalEntry[];
  accounts: LedgerAccount[];
  // Transactions that already belong to some planning target, by id, with the
  // name of the owner, so the dialog can say why one is not on offer.
  owners: Map<string, string>;
  onNotify: (message: string) => void;
  onChanged: () => Promise<void>;
}

interface Candidate {
  id: string;
  label: string;
  when: string;
  amount: string;
}

// One payment is often several transactions: a subscription and the top-up
// that moved it to another plan. They are picked together and each becomes
// its own assignment, because a transaction belongs to a plan in full and a
// plan may own many transactions.
export function AssignDialog({ open, onClose, bookID, view, transactions, accounts, owners, onNotify, onChanged }: AssignDialogProps) {
  const item = view.item;
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);

  const accountIDs = useMemo(() => new Set(accounts.map((account) => account.id)), [accounts]);

  const candidates: Candidate[] = useMemo(() => transactions
    // A transaction that already has an owner is not on offer: it has one
    // planning owner at most, and moving it is a decision of its own.
    .filter((entry) => !owners.has(entry.transaction.id))
    .filter((entry) => (
      (item.kind === "expense" && entry.transaction.operation === "manual.expense") ||
      (item.kind === "income" && entry.transaction.operation === "manual.income") ||
      !entry.transaction.operation))
    .map((entry) => {
      const economic = entry.postings.find((posting) => !accountIDs.has(posting.accountId)) ?? entry.postings[0];
      return {
        id: entry.transaction.id,
        label: payeeName(entry.transaction.description || "Transaction"),
        when: new Date(entry.transaction.effectiveAt).toLocaleDateString("en", { day: "2-digit", month: "short" }),
        amount: money(economic.amount.minor, economic.amount.currency),
      };
    }), [transactions, owners, item.kind, accountIDs]);

  const shown = useMemo(
    () => rankedFilter(candidates, query, (candidate) => [candidate.label, candidate.amount]),
    [candidates, query],
  );

  function toggle(id: string) {
    setPicked((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  }

  function close() {
    setPicked([]);
    setQuery("");
    setFailure("");
    onClose();
  }

  async function submit() {
    setSaving(true);
    setFailure("");
    let done = 0;
    try {
      for (const id of picked) {
        await assignTransaction(bookID, id, { planItemId: item.id });
        done += 1;
      }
      await onChanged();
      onNotify(done === 1 ? `1 transaction now belongs to "${item.name}".` : `${done} transactions now belong to "${item.name}".`);
      close();
    } catch (error) {
      await onChanged();
      setFailure(done === 0
        ? humanError(error)
        : `${done} of ${picked.length} were assigned, then it stopped: ${humanError(error)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open} onClose={close} title="Assign transactions"
      subtitle={`Pick every transaction that belongs to "${item.name}". A transaction belongs to a plan in full, so there is no amount to split.`}
      footer={<>
        <button className="secondary-button" type="button" onClick={close}>Cancel</button>
        <button className="primary-button compact" type="button" disabled={saving || picked.length === 0} onClick={submit}>
          <span>{saving ? "Assigning…" : picked.length > 1 ? `Assign ${picked.length} transactions` : "Assign"}</span>
          <span aria-hidden="true">→</span>
        </button>
      </>}
    >
      {candidates.length === 0 ? (
        <p className="panel-note">Nothing is available: every {item.kind} of this period already belongs to a plan or a group.</p>
      ) : (
        <>
          <div className="pick-search">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input
              type="search" value={query} autoComplete="off" aria-label="Search transactions"
              placeholder={`Search ${candidates.length} unassigned ${candidates.length === 1 ? "transaction" : "transactions"}`}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <ul className="pick-list">
            {shown.map((candidate) => (
              <li key={candidate.id} className={`pick-row ${picked.includes(candidate.id) ? "picked" : ""}`}>
                <label className="pick-main">
                  <input type="checkbox" checked={picked.includes(candidate.id)} onChange={() => toggle(candidate.id)} />
                  <span className="pick-copy">
                    <strong title={candidate.label}>{candidate.label}</strong>
                    <small>{candidate.when}</small>
                  </span>
                  <span className="pick-amount">{candidate.amount}</span>
                </label>
              </li>
            ))}
            {shown.length === 0 ? <li className="pick-empty">Nothing matches “{query.trim()}”.</li> : null}
          </ul>
          <dl className="pick-total">
            <div><dt>Planned</dt><dd>{money(item.amount.minor, item.amount.currency)}</dd></div>
            <div><dt>Already assigned</dt><dd>{money(view.allocated.minor, view.allocated.currency)}</dd></div>
            <div><dt>Adding</dt><dd>{picked.length === 0 ? "—" : `${picked.length} ${picked.length === 1 ? "transaction" : "transactions"}`}</dd></div>
          </dl>
          {failure ? <p className="form-error" role="alert">{failure}</p> : null}
        </>
      )}
    </Modal>
  );
}
